/**
 * BeliefInspector Page (SPEC §2.6 item 7).
 *
 * Inspect what each agent currently believes: statement, evidence timeline,
 * stored vs decayed confidence, age, retest date. Pin / correct / retire are
 * the human ground-truth levers — they flow through the backend
 * BeliefService, which records human evidence and keeps the retest floor.
 *
 * Two deliberate semantics surfaced (not bugs):
 * - Rewriting a statement detaches the belief from the collective tier.
 * - Human pins/corrections never count toward cross-tenant promotion.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  EmptyState,
  InlineStack,
  Layout,
  Modal,
  Page,
  Select,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import type { Belief, BeliefDetail } from '../types/beliefs';
import {
  formatConfidence,
  getBeliefStatusLabel,
  getBeliefStatusTone,
} from '../types/beliefs';
import {
  correctBelief,
  getBelief,
  listBeliefs,
  pinBelief,
  retireBelief,
} from '../services/beliefsApi';

const domainOptions = [
  { label: 'All Agents', value: '' },
  { label: 'Product Marketing', value: 'product_marketing' },
  { label: 'Demand Gen', value: 'demand_gen' },
  { label: 'Paid Media', value: 'paid_media' },
  { label: 'Content', value: 'content' },
  { label: 'SEO', value: 'seo' },
  { label: 'Lifecycle', value: 'lifecycle' },
  { label: 'ABM', value: 'abm' },
  { label: 'SDR', value: 'sdr' },
  { label: 'RevOps', value: 'revops' },
  { label: 'Orchestrator', value: 'orchestrator' },
];

const statusOptions = [
  { label: 'All Statuses', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Under Test', value: 'under_test' },
  { label: 'Stale', value: 'stale' },
  { label: 'Retired', value: 'retired' },
];

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
}

interface BeliefCardProps {
  belief: Belief;
  onInspect: (id: string) => void;
}

function BeliefCard({ belief, onInspect }: BeliefCardProps) {
  const decayGap = belief.stored_confidence - belief.decayed_confidence;
  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="start">
          <InlineStack gap="200" blockAlign="center">
            <Badge tone={getBeliefStatusTone(belief.status)}>
              {getBeliefStatusLabel(belief.status)}
            </Badge>
            <Badge>{belief.domain.replace(/_/g, ' ')}</Badge>
            {belief.collective_eligible && belief.pattern_key && (
              <Badge tone="info">Collective-eligible</Badge>
            )}
            {belief.scope === 'industry' && (
              <Badge tone="attention">Industry prior</Badge>
            )}
          </InlineStack>
          <Button variant="plain" onClick={() => onInspect(belief.id)}>
            Inspect
          </Button>
        </InlineStack>

        <Text as="p" variant="bodyMd">
          {belief.statement}
        </Text>

        <InlineStack gap="400">
          <Text as="span" variant="bodySm" tone="subdued">
            Stored: {formatConfidence(belief.stored_confidence)}
          </Text>
          <Text
            as="span"
            variant="bodySm"
            tone={decayGap > 0.05 ? 'caution' : 'subdued'}
          >
            Now (decayed): {formatConfidence(belief.decayed_confidence)}
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            Age:{' '}
            {belief.age_days != null ? `${belief.age_days}d` : 'never tested'}
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            Retest by: {formatDate(belief.retest_by)}
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            Evidence: {belief.evidence_count}
          </Text>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

type ActionMode = 'pin' | 'correct' | 'retire' | null;

export function BeliefInspector() {
  const [beliefs, setBeliefs] = useState<Belief[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [domainFilter, setDomainFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [detail, setDetail] = useState<BeliefDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [actionMode, setActionMode] = useState<ActionMode>(null);
  const [actionStatement, setActionStatement] = useState('');
  const [actionConfidence, setActionConfidence] = useState('100');
  const [isActing, setIsActing] = useState(false);

  const fetchBeliefs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await listBeliefs({
        domain: (domainFilter || undefined) as Belief['domain'] | undefined,
        status: (statusFilter || undefined) as Belief['status'] | undefined,
        limit: 50,
      });
      setBeliefs(response.beliefs);
    } catch (err) {
      console.error('Failed to load beliefs:', err);
      setError('Failed to load beliefs. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [domainFilter, statusFilter]);

  useEffect(() => {
    fetchBeliefs();
  }, [fetchBeliefs]);

  const handleInspect = async (id: string) => {
    setIsLoadingDetail(true);
    setActionMode(null);
    try {
      const data = await getBelief(id);
      setDetail(data);
      setActionStatement(data.statement);
      setActionConfidence(String(Math.round(data.stored_confidence * 100)));
    } catch (err) {
      console.error('Failed to load belief detail:', err);
      setError('Failed to load belief detail.');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setActionMode(null);
  };

  // Deep link: /beliefs?belief=<id> auto-opens the inspector modal (used by
  // "View belief in inspector" links on proposal cards). Read once on mount.
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    deepLinkHandled.current = true;
    const beliefId = new URLSearchParams(window.location.search).get('belief');
    if (beliefId) {
      handleInspect(beliefId);
    }
  }, []);

  const handleAction = async () => {
    if (!detail || !actionMode) return;
    setIsActing(true);
    setError(null);
    try {
      const statementChanged = actionStatement !== detail.statement;
      let response;
      if (actionMode === 'pin') {
        response = await pinBelief(
          detail.id,
          Number(actionConfidence) / 100,
          statementChanged ? actionStatement : undefined
        );
      } else if (actionMode === 'correct') {
        response = await correctBelief(detail.id, {
          statement: statementChanged ? actionStatement : undefined,
          confidence: Number(actionConfidence) / 100,
        });
      } else {
        response = await retireBelief(detail.id);
      }

      setDetail(response.belief);
      setBeliefs(prev =>
        prev.map(b => (b.id === response.belief.id ? response.belief : b))
      );
      setActionMode(null);
      if (response.detached_from_collective) {
        setNotice(
          'Statement rewritten: this belief has been detached from the ' +
            'collective (industry) tier. A reworded belief no longer ' +
            'expresses the shared pattern, so it stops contributing to ' +
            'cross-tenant promotion. This is deliberate.'
        );
      } else if (actionMode === 'pin') {
        setNotice(
          'Belief pinned as ground truth. It will still be re-surfaced for ' +
            'validation within the 5-month retest floor.'
        );
      }
    } catch (err) {
      const detailMsg =
        (err as Error & { detail?: string }).detail || 'Please try again.';
      setError(`Action failed: ${detailMsg}`);
    } finally {
      setIsActing(false);
    }
  };

  return (
    <Page
      title="Belief Inspector"
      subtitle="What the agents currently believe — dated, decaying, and inspectable"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                {error}
              </Banner>
            )}
            {notice && (
              <Banner tone="info" onDismiss={() => setNotice(null)}>
                {notice}
              </Banner>
            )}

            <Card>
              <InlineStack gap="400">
                <Select
                  label="Agent"
                  labelInline
                  options={domainOptions}
                  value={domainFilter}
                  onChange={setDomainFilter}
                />
                <Select
                  label="Status"
                  labelInline
                  options={statusOptions}
                  value={statusFilter}
                  onChange={setStatusFilter}
                />
              </InlineStack>
            </Card>

            {isLoading && (
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            )}

            {!isLoading && beliefs.length === 0 && (
              <Card>
                <EmptyState heading="No beliefs yet" image="">
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Beliefs appear here once agents are enabled and their
                    starter hypotheses are authored.
                  </Text>
                </EmptyState>
              </Card>
            )}

            {!isLoading &&
              beliefs.map(belief => (
                <BeliefCard
                  key={belief.id}
                  belief={belief}
                  onInspect={handleInspect}
                />
              ))}
          </BlockStack>
        </Layout.Section>
      </Layout>

      {/* Detail modal with evidence timeline + actions */}
      <Modal
        open={detail !== null || isLoadingDetail}
        onClose={closeDetail}
        title="Belief detail"
        size="large"
      >
        <Modal.Section>
          {isLoadingDetail && (
            <InlineStack align="center">
              <Spinner size="large" />
            </InlineStack>
          )}
          {detail && (
            <BlockStack gap="300">
              <Text as="p" variant="bodyLg">
                {detail.statement}
              </Text>
              <InlineStack gap="400">
                <Text as="span" variant="bodySm">
                  Stored confidence:{' '}
                  {formatConfidence(detail.stored_confidence)}
                </Text>
                <Text as="span" variant="bodySm">
                  Decayed (now): {formatConfidence(detail.decayed_confidence)}
                </Text>
                <Text as="span" variant="bodySm">
                  Half-life: {detail.decay_half_life_days}d
                </Text>
                <Text as="span" variant="bodySm">
                  Retest by: {formatDate(detail.retest_by)}
                </Text>
              </InlineStack>

              {detail.collective_eligible && detail.pattern_key && (
                <Banner tone="info">
                  <Text as="p" variant="bodySm">
                    This belief expresses a shared industry pattern (
                    {detail.pattern_key}). Rewriting its statement will detach
                    it from the collective tier. Note: human pins and
                    corrections apply to your tenant only — they never count
                    toward cross-tenant promotion.
                  </Text>
                </Banner>
              )}

              <Divider />

              <Text as="h3" variant="headingSm">
                Evidence timeline
              </Text>
              {detail.evidence.length === 0 && (
                <Text as="p" variant="bodySm" tone="subdued">
                  No evidence yet — this belief has never been tested.
                </Text>
              )}
              {detail.evidence.map(evidence => (
                <Box
                  key={evidence.id}
                  paddingBlockStart="100"
                  paddingBlockEnd="100"
                >
                  <BlockStack gap="050">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" variant="bodySm" fontWeight="semibold">
                        {formatDate(evidence.observed_at)}
                      </Text>
                      {evidence.is_human && (
                        <Badge tone="attention">Human</Badge>
                      )}
                      <Text as="span" variant="bodySm">
                        → {formatConfidence(evidence.confidence_after)}
                      </Text>
                      {evidence.sample_size != null && (
                        <Text as="span" variant="bodySm" tone="subdued">
                          n={evidence.sample_size}
                        </Text>
                      )}
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {evidence.test_description}
                      {evidence.result ? ` — ${evidence.result}` : ''}
                    </Text>
                  </BlockStack>
                </Box>
              ))}

              {detail.proposals.length > 0 && (
                <>
                  <Divider />
                  <Text as="h3" variant="headingSm">
                    Tests run from this belief
                  </Text>
                  {detail.proposals.map(proposalRef => (
                    <InlineStack key={proposalRef.id} gap="200" blockAlign="center">
                      <Text as="span" variant="bodySm">
                        {proposalRef.action_type.replace(/_/g, ' ')}
                      </Text>
                      <Badge>{proposalRef.status}</Badge>
                      <Badge tone={proposalRef.tier === 'tier1_auto' ? 'info' : 'attention'}>
                        {proposalRef.tier === 'tier1_auto' ? 'Tier 1' : 'Tier 2'}
                      </Badge>
                      <Button variant="plain" url="/approvals">
                        View in approvals
                      </Button>
                    </InlineStack>
                  ))}
                </>
              )}

              <Divider />

              {actionMode === null && (
                <InlineStack gap="200">
                  <Button
                    onClick={() => {
                      // Ground truth defaults to full confidence; the field
                      // stays editable for softer pins.
                      setActionConfidence('100');
                      setActionMode('pin');
                    }}
                  >
                    Pin as ground truth
                  </Button>
                  <Button
                    onClick={() => {
                      setActionConfidence(
                        String(Math.round(detail.stored_confidence * 100))
                      );
                      setActionMode('correct');
                    }}
                  >
                    Correct
                  </Button>
                  <Button
                    tone="critical"
                    onClick={() => setActionMode('retire')}
                    disabled={detail.status === 'retired'}
                  >
                    Retire
                  </Button>
                </InlineStack>
              )}

              {actionMode === 'retire' && (
                <BlockStack gap="200">
                  <Banner tone="warning">
                    <Text as="p" variant="bodySm">
                      Retiring removes this belief from the loop. Its evidence
                      history is kept.
                    </Text>
                  </Banner>
                  <InlineStack gap="200">
                    <Button
                      tone="critical"
                      variant="primary"
                      onClick={handleAction}
                      loading={isActing}
                    >
                      Confirm retire
                    </Button>
                    <Button onClick={() => setActionMode(null)}>Cancel</Button>
                  </InlineStack>
                </BlockStack>
              )}

              {(actionMode === 'pin' || actionMode === 'correct') && (
                <BlockStack gap="200">
                  <TextField
                    label="Statement"
                    value={actionStatement}
                    onChange={setActionStatement}
                    multiline={2}
                    autoComplete="off"
                    helpText={
                      detail.pattern_key
                        ? 'Rewriting the statement detaches this belief from the collective tier (deliberate).'
                        : undefined
                    }
                  />
                  <TextField
                    label="Confidence (%)"
                    type="number"
                    value={actionConfidence}
                    onChange={setActionConfidence}
                    autoComplete="off"
                    min={0}
                    max={100}
                  />
                  <Banner tone="info">
                    <Text as="p" variant="bodySm">
                      {actionMode === 'pin'
                        ? 'Pinning records human ground truth as evidence. The belief will still be re-surfaced for validation within the 5-month retest floor — pinning is never an exemption.'
                        : 'A correction records human evidence at the confidence you set.'}
                    </Text>
                  </Banner>
                  <InlineStack gap="200">
                    <Button
                      variant="primary"
                      onClick={handleAction}
                      loading={isActing}
                    >
                      {actionMode === 'pin' ? 'Pin belief' : 'Save correction'}
                    </Button>
                    <Button onClick={() => setActionMode(null)}>Cancel</Button>
                  </InlineStack>
                </BlockStack>
              )}
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}

export default BeliefInspector;
